import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, FileText, Loader2, Pencil, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIERS, TIER_LABEL, TIER_CLS } from "@/components/ProductVariants";

const FIELDS = [
  ["company_name", "Company Name", true], ["email", "Email (login)", true, "email"], ["address", "Address"],
  ["ssm_no", "SSM No."], ["tin_no", "TIN No."], ["sst_no", "SST Reg No."],
  ["pic_name", "PIC Name", true], ["pic_phone", "PIC Tel No."], ["pic_email", "PIC Email", false, "email"],
  ["password", "Password (min 6)", true, "password"],
];
const empty = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

export const openSsm = async (a) => {
  try {
    const r = await api.get(`/users/${a.id}/ssm`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  } catch { toast.error("SSM certificate not available"); }
};

export const CreateAgentDialog = ({ reload }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("ssm_file", file);
      await api.post("/agents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Agent created & approved");
      setOpen(false); setForm(empty); setFile(null); reload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to create agent");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="create-agent-btn"><UserPlus className="w-4 h-4 mr-1" /> Create Agent</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create Agent Account</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, label, req, type]) => (
            <div key={k} className={["company_name", "email", "address"].includes(k) ? "col-span-2" : ""}>
              <Label>{label}{req && " *"}</Label>
              <Input type={type || "text"} required={!!req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`create-agent-${k.replace(/_/g, "-")}`} />
            </div>
          ))}
          <div className="col-span-2">
            <Label>SSM Certificate (optional, PDF/image)</Label>
            <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="create-agent-ssm-file" />
          </div>
          <Button type="submit" disabled={loading} className="col-span-2 bg-cyan-600 hover:bg-cyan-700" data-testid="create-agent-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create (auto-approved)"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-border/60 text-sm">
    <span className="text-muted-foreground">{k}</span><span className="font-medium text-right break-all">{v || "-"}</span>
  </div>
);

const EDIT_FIELDS = [
  ["company", "Company Name"], ["address", "Address"], ["ssm_no", "SSM No."], ["tin_no", "TIN No."], ["sst_no", "SST Reg No."],
  ["name", "PIC Name"], ["phone", "PIC Tel No."], ["pic_email", "PIC Email", "email"],
];

export const AgentDetailDialog = ({ agent, open, onClose, isAdmin, reload }) => {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (agent) { setForm(Object.fromEntries(EDIT_FIELDS.map(([k]) => [k, agent[k] || ""]))); setEdit(false); setPw(""); } }, [agent]);
  if (!agent) return null;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (pw) body.password = pw;
      await api.patch(`/users/${agent.id}`, body);
      toast.success(pw ? "Agent updated & password reset" : "Agent updated");
      setEdit(false); setPw(""); reload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const setTier = async (tier) => {
    await api.patch(`/users/${agent.id}`, { tier });
    toast.success(`Tier set to ${TIER_LABEL[tier]}`);
    reload?.();
  };

  const tier = agent.tier || "normal";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="agent-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">{agent.company || agent.name}
            {agent.approved ? <Badge className="bg-emerald-600">Approved</Badge> : <Badge variant="secondary">Pending</Badge>}
            {!agent.active && <Badge variant="destructive">Disabled</Badge>}
            <Badge className={TIER_CLS[tier]} data-testid="agent-tier-badge">{TIER_LABEL[tier]}</Badge>
          </DialogTitle>
        </DialogHeader>

        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs">Agent Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="w-36 h-8" data-testid="agent-tier-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{TIER_LABEL[t]}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant={edit ? "secondary" : "outline"} className="ml-auto" onClick={() => setEdit(!edit)} data-testid="agent-edit-toggle"><Pencil className="w-3.5 h-3.5 mr-1" /> {edit ? "Cancel" : "Edit"}</Button>
          </div>
        )}

        {edit ? (
          <form onSubmit={save} className="grid grid-cols-2 gap-3" data-testid="agent-edit-form">
            {EDIT_FIELDS.map(([k, label, type]) => (
              <div key={k} className={["company", "address"].includes(k) ? "col-span-2" : ""}>
                <Label>{label}</Label>
                <Input type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`agent-edit-${k.replace(/_/g, "-")}`} />
              </div>
            ))}
            <div className="col-span-2 border-t pt-3">
              <Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Reset password (leave blank to keep)</Label>
              <Input type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 6)" data-testid="agent-edit-password" />
            </div>
            <Button type="submit" disabled={saving} className="col-span-2 bg-cyan-600 hover:bg-cyan-700" data-testid="agent-edit-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button>
          </form>
        ) : (
          <>
            <div className="text-xs font-mono uppercase text-amber-600 mt-1">Company</div>
            <Row k="Company Name" v={agent.company} />
            <Row k="Login Email" v={agent.email} />
            <Row k="Address" v={agent.address} />
            <Row k="SSM No." v={agent.ssm_no} />
            <Row k="TIN No." v={agent.tin_no} />
            <Row k="SST Reg No." v={agent.sst_no} />
            <div className="text-xs font-mono uppercase text-amber-600 mt-3">Person In Charge</div>
            <Row k="PIC Name" v={agent.name} />
            <Row k="PIC Tel No." v={agent.phone} />
            <Row k="PIC Email" v={agent.pic_email} />
            <div className="text-xs font-mono uppercase text-amber-600 mt-3">Account</div>
            <Row k="Registered" v={agent.created_at ? new Date(agent.created_at).toLocaleString("en-MY") : "-"} />
            <Row k="Created by" v={agent.created_by_admin ? `Admin (${agent.created_by_admin})` : "Self-registered"} />
            <Row k="Agreed T&C" v={agent.agreed_terms_at ? new Date(agent.agreed_terms_at).toLocaleString("en-MY") : "-"} />
            <div className="pt-3">
              {agent.ssm_file
                ? <Button variant="outline" size="sm" onClick={() => openSsm(agent)} data-testid="detail-view-ssm"><FileText className="w-4 h-4 mr-1" /> View SSM Certificate ({agent.ssm_file.filename})</Button>
                : <span className="text-xs text-muted-foreground">No SSM certificate uploaded</span>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
